import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../scss/OperationsLog.scss';
import BaseURL from '../assets/contants/BaseURL';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserCircle, 
  faCaretDown, 
  faUser, 
  faSyncAlt, 
  faSignOutAlt, 
  faFilter, 
  faRedo, 
  faArrowUp, 
  faExchangeAlt, 
  faArrowsAltH, 
  faRandom, 
  faClock, 
  faWeightHanging, 
  faAngleDoubleLeft, 
  faAngleLeft, 
  faAngleRight, 
  faAngleDoubleRight, 
  faChartGantt, 
  faCalendarAlt, 
  faBuilding, 
  faExclamationTriangle,
  faCrown 
} from '@fortawesome/free-solid-svg-icons';

const OperationsLog = () => {
    const navigate = useNavigate();
    const [cranes, setCranes] = useState([]);
    const [selectedCrane, setSelectedCrane] = useState('all');
    const [selectedOperationType, setSelectedOperationType] = useState('all');
    const [selectedDateRange, setSelectedDateRange] = useState('today');
    const [metrics, setMetrics] = useState({
        hoist_up: 0,
        hoist_down: 0,
        ct_left: 0,
        ct_right: 0,
        lt_forward: 0,
        lt_reverse: 0,
        total_hoist: 0,
        total_ct: 0,
        total_lt: 0,
        emergency: 0,
        total_duration: '0:00:00',
        total_load_weight: 0
    });
    const [operations, setOperations] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const recordsPerPage = 10;
    const [ganttData, setGanttData] = useState(null);
    const [selectedGanttDate, setSelectedGanttDate] = useState(new Date().toISOString().split('T')[0]);
    const [isGanttLoading, setIsGanttLoading] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState({ cranes: [], overall: 'disconnected', message: 'Connecting...' });
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [orgLogo, setOrgLogo] = useState(null);
    
    // WebSocket refs
    const operationsWebSocketRef = useRef(null);
    const tableWebSocketRef = useRef(null);
    const ganttWebSocketRef = useRef(null);
    
    // Page refs
    const currentPageRef = useRef(1);
    
    // Connection flags
    const reconnectAttemptsRef = useRef({
        operations: 0,
        table: 0,
        gantt: 0
    });
    
    const reconnectTimeoutsRef = useRef({
        operations: null,
        table: null,
        gantt: null
    });

    const heartbeatIntervalsRef = useRef({
        operations: null,
        table: null,
        gantt: null
    });

    const isUnmountingRef = useRef(false);
    const isConnectingRef = useRef({
        operations: false,
        table: false,
        gantt: false
    });

    // Keep ref in sync with state
    useEffect(() => {
        currentPageRef.current = currentPage;
    }, [currentPage]);

    const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');
    const getSessionID = () => sessionStorage.getItem('session_id') || localStorage.getItem('session_id');

    useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);

    const getWebSocketUrl = (endpoint) => {
        const token = getAuthToken();
        const session_id = getSessionID();

        if (!token || !session_id) {
            console.warn(`Missing auth credentials for ${endpoint}`);
            return null;
        }

        // Use wss for production, ws for development
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        
        // Extract host from BaseURL
        let host = BaseURL.replace('http://', '').replace('https://', '').replace(/\/$/, '');
        
        return `${protocol}//${host}/data/${endpoint}/?token=${encodeURIComponent(token)}&session_id=${encodeURIComponent(session_id)}`;
    };

    const formatDuration = (seconds) => {
        if (!seconds) return '0m';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    };

    const formatDurationWithSeconds = (seconds) => {
        if (!seconds) return '0s';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        let result = '';
        if (hours > 0) result += `${hours}h `;
        if (minutes > 0 || hours > 0) result += `${minutes}m `;
        result += `${secs}s`;
        return result;
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        return timestamp.replace('T', ' ');
    };

    const showNotification = (message, type = 'success') => {
        console.log(`[${type.toUpperCase()}] ${message}`);
    };

    const getOperationTypeClass = (operationType) => {
        const typeMap = {
            'Hoist Up': 'type-hoist-up',
            'Hoist Down': 'type-hoist-downs',
            'CT Left': 'type-ct-lefts',
            'CT Right': 'type-ct-rights',
            'LT Forward': 'type-lt-forwards',
            'LT Reverse': 'type-lt-reverse',
            'Emergency': 'type-emergency'
        };
        return typeMap[operationType] || '';
    };

    const clearReconnectTimeout = (type) => {
        if (reconnectTimeoutsRef.current[type]) {
            clearTimeout(reconnectTimeoutsRef.current[type]);
            reconnectTimeoutsRef.current[type] = null;
        }
    };

    const clearHeartbeat = (type) => {
        if (heartbeatIntervalsRef.current[type]) {
            clearInterval(heartbeatIntervalsRef.current[type]);
            heartbeatIntervalsRef.current[type] = null;
        }
    };

    const setupHeartbeat = (ws, type) => {
        clearHeartbeat(type);

        heartbeatIntervalsRef.current[type] = setInterval(() => {
            if (ws && ws.readyState === WebSocket.OPEN) {
                try {
                    ws.send(JSON.stringify({ type: 'ping' }));
                } catch (error) {
                    console.error(`Heartbeat ping failed for ${type}:`, error);
                }
            }
        }, 30000);
    };

    const fetchCranes = useCallback(async () => {
        const token = getAuthToken();

        if (!token) {
            console.error('No authentication token found');
            return;
        }

        try {
            const url = `${BaseURL}meters/cms-cranes/`;
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Token ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            setCranes(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching cranes:', error);
        }
    }, []);

    const updateConnectionStatus = useCallback((statusData) => {
        if (!statusData || !statusData.cranes || statusData.cranes.length === 0) {
            setConnectionStatus({
                cranes: [],
                overall: 'disconnected',
                message: 'No cranes available'
            });
            return;
        }

        const cranesList = statusData.cranes;
        const onlineCount = cranesList.filter(crane => crane.connection_status === 'online').length;
        const totalCount = cranesList.length;

        let overall = 'disconnected';
        let message = '';

        if (totalCount === 0) {
            overall = 'disconnected';
            message = 'No cranes available';
        } else if (onlineCount === totalCount) {
            overall = 'connected';
            message = 'Connected';
        } else if (onlineCount > 0) {
            overall = 'partial';
            message = `${onlineCount}/${totalCount} cranes Connected`;
        } else {
            overall = 'disconnected';
            message = 'Disconnected';
        }

        setConnectionStatus({ cranes: cranesList, overall, message });
    }, []);

    const updateMetrics = useCallback((data) => {
        setMetrics(prev => ({
            ...prev,
            hoist_up: data.hoist_up || 0,
            hoist_down: data.hoist_down || 0,
            ct_left: data.ct_left || 0,
            ct_right: data.ct_right || 0,
            lt_forward: data.lt_forward || 0,
            lt_reverse: data.lt_reverse || 0,
            total_hoist: data.total_hoist || 0,
            total_ct: data.total_ct || 0,
            total_lt: data.total_lt || 0,
            emergency: data.emergency || 0,
            total_duration: data.total_duration || '0:00:00',
            total_load_weight: data.total_load_weight || 0
        }));
    }, []);

    const sendTableFilterRequest = useCallback((page) => {
        if (!tableWebSocketRef.current || tableWebSocketRef.current.readyState !== WebSocket.OPEN) {
            console.log('Table WebSocket not open, waiting for connection');
            return;
        }
        
        const message = {
            type: 'filter',
            crane_id: selectedCrane,
            operation_type: selectedOperationType,
            date_range: selectedDateRange,
            page: page,
            page_size: recordsPerPage
        };
        console.log('Sending table filter request:', message);
        tableWebSocketRef.current.send(JSON.stringify(message));
    }, [selectedCrane, selectedOperationType, selectedDateRange]);

    const connectOperationsWebSocket = useCallback(() => {
        // Prevent multiple connection attempts
        if (isUnmountingRef.current || isConnectingRef.current.operations) {
            console.log('Operations WebSocket: Already connecting or unmounting');
            return;
        }
        
        const wsUrl = getWebSocketUrl('crane-operations');
        if (!wsUrl) {
            console.error('Operations WebSocket: Invalid URL');
            return;
        }

        console.log('Operations WebSocket: Connecting to', wsUrl);
        isConnectingRef.current.operations = true;

        // Close existing connection
        if (operationsWebSocketRef.current) {
            const oldWs = operationsWebSocketRef.current;
            operationsWebSocketRef.current = null;
            if (oldWs.readyState === WebSocket.OPEN || oldWs.readyState === WebSocket.CONNECTING) {
                oldWs.close(1000, "New connection established");
            }
        }

        clearHeartbeat('operations');
        clearReconnectTimeout('operations');
        
        const ws = new WebSocket(wsUrl);
        operationsWebSocketRef.current = ws;

        // Connection timeout
        const connectionTimeout = setTimeout(() => {
            if (ws.readyState !== WebSocket.OPEN) {
                console.error('Operations WebSocket: Connection timeout');
                ws.close();
                isConnectingRef.current.operations = false;
            }
        }, 10000);

        ws.onopen = () => {
            clearTimeout(connectionTimeout);
            console.log('Operations WebSocket: Connected successfully');
            isConnectingRef.current.operations = false;
            reconnectAttemptsRef.current.operations = 0;

            setupHeartbeat(ws, 'operations');

            // Send initial filter request
            ws.send(JSON.stringify({
                type: 'apply_filters',
                crane_id: selectedCrane,
                operation_type: selectedOperationType,
                date_range: selectedDateRange
            }));
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('Operations WebSocket: Received message', data.type);
                
                if (data.type === 'pong') return;
                if (data.type === 'operations_update') {
                    updateMetrics(data.data);
                    if (data.connection_status) {
                        updateConnectionStatus(data.connection_status);
                    }
                }
            } catch (error) {
                console.error('Operations WebSocket: Error parsing message', error);
            }
        };

        ws.onclose = (event) => {
            clearTimeout(connectionTimeout);
            clearHeartbeat('operations');
            console.log(`Operations WebSocket: Disconnected (code: ${event.code}, reason: ${event.reason})`);
            isConnectingRef.current.operations = false;

            // Reconnect logic
            if (!isUnmountingRef.current && event.code !== 1000 && reconnectAttemptsRef.current.operations < 5) {
                const delay = Math.min(5000 * Math.pow(2, reconnectAttemptsRef.current.operations), 30000);
                console.log(`Operations WebSocket: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current.operations + 1}/5)`);
                
                clearReconnectTimeout('operations');
                reconnectTimeoutsRef.current.operations = setTimeout(() => {
                    reconnectAttemptsRef.current.operations++;
                    connectOperationsWebSocket();
                }, delay);
            }
        };

        ws.onerror = (error) => {
            console.error('Operations WebSocket: Error', error);
        };
    }, [selectedCrane, selectedOperationType, selectedDateRange, updateMetrics, updateConnectionStatus]);

    const connectTableWebSocket = useCallback(() => {
        // Prevent multiple connection attempts
        if (isUnmountingRef.current || isConnectingRef.current.table) {
            console.log('Table WebSocket: Already connecting or unmounting');
            return;
        }
        
        const wsUrl = getWebSocketUrl('crane-operations-table');
        if (!wsUrl) {
            console.error('Table WebSocket: Invalid URL');
            return;
        }

        console.log('Table WebSocket: Connecting to', wsUrl);
        isConnectingRef.current.table = true;

        // Close existing connection
        if (tableWebSocketRef.current) {
            const oldWs = tableWebSocketRef.current;
            tableWebSocketRef.current = null;
            if (oldWs.readyState === WebSocket.OPEN || oldWs.readyState === WebSocket.CONNECTING) {
                oldWs.close(1000, "New connection established");
            }
        }

        clearHeartbeat('table');
        clearReconnectTimeout('table');
        
        const ws = new WebSocket(wsUrl);
        tableWebSocketRef.current = ws;

        // Connection timeout
        const connectionTimeout = setTimeout(() => {
            if (ws.readyState !== WebSocket.OPEN) {
                console.error('Table WebSocket: Connection timeout');
                ws.close();
                isConnectingRef.current.table = false;
            }
        }, 10000);

        ws.onopen = () => {
            clearTimeout(connectionTimeout);
            console.log('Table WebSocket: Connected successfully');
            isConnectingRef.current.table = false;
            reconnectAttemptsRef.current.table = 0;
            
            setupHeartbeat(ws, 'table');

            // Send initial request with current page
            sendTableFilterRequest(currentPageRef.current);
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('Table WebSocket: Received message', data.type);
                
                if (data.type === 'pong') return;

                if (data.type === 'table_update') {
                    setOperations(data.data || []);
                    if (data.pagination) {
                        setTotalRecords(data.pagination.total_count || 0);
                        setTotalPages(data.pagination.total_pages || 1);
                        if (data.pagination.page && data.pagination.page !== currentPageRef.current) {
                            setCurrentPage(data.pagination.page);
                            currentPageRef.current = data.pagination.page;
                        }
                    }
                }
            } catch (error) {
                console.error('Table WebSocket: Error parsing message', error);
            }
        };

        ws.onclose = (event) => {
            clearTimeout(connectionTimeout);
            clearHeartbeat('table');
            console.log(`Table WebSocket: Disconnected (code: ${event.code}, reason: ${event.reason})`);
            isConnectingRef.current.table = false;

            // Reconnect logic
            if (!isUnmountingRef.current && event.code !== 1000 && reconnectAttemptsRef.current.table < 5) {
                const delay = Math.min(5000 * Math.pow(2, reconnectAttemptsRef.current.table), 30000);
                console.log(`Table WebSocket: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current.table + 1}/5)`);
                
                clearReconnectTimeout('table');
                reconnectTimeoutsRef.current.table = setTimeout(() => {
                    reconnectAttemptsRef.current.table++;
                    connectTableWebSocket();
                }, delay);
            }
        };

        ws.onerror = (error) => {
            console.error('Table WebSocket: Error', error);
        };
    }, [selectedCrane, selectedOperationType, selectedDateRange, sendTableFilterRequest]);

    const connectGanttWebSocket = useCallback(() => {
        // Prevent multiple connection attempts
        if (isUnmountingRef.current || isConnectingRef.current.gantt) {
            console.log('Gantt WebSocket: Already connecting or unmounting');
            return;
        }
        
        const wsUrl = getWebSocketUrl('gantt-operation-log');
        if (!wsUrl) {
            console.error('Gantt WebSocket: Invalid URL');
            return;
        }

        console.log('Gantt WebSocket: Connecting to', wsUrl);
        isConnectingRef.current.gantt = true;

        // Close existing connection
        if (ganttWebSocketRef.current) {
            const oldWs = ganttWebSocketRef.current;
            ganttWebSocketRef.current = null;
            if (oldWs.readyState === WebSocket.OPEN || oldWs.readyState === WebSocket.CONNECTING) {
                oldWs.close(1000, "New connection established");
            }
        }

        const ws = new WebSocket(wsUrl);
        ganttWebSocketRef.current = ws;

        // Connection timeout
        const connectionTimeout = setTimeout(() => {
            if (ws.readyState !== WebSocket.OPEN) {
                console.error('Gantt WebSocket: Connection timeout');
                ws.close();
                isConnectingRef.current.gantt = false;
            }
        }, 10000);

        ws.onopen = () => {
            clearTimeout(connectionTimeout);
            console.log('Gantt WebSocket: Connected successfully');
            isConnectingRef.current.gantt = false;
            reconnectAttemptsRef.current.gantt = 0;

            ws.send(JSON.stringify({
                type: 'get_gantt_data',
                crane_id: selectedCrane === 'all' ? '' : selectedCrane,
                date: selectedGanttDate,
                operation_type: selectedOperationType === 'all' ? '' : selectedOperationType
            }));
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('Gantt WebSocket: Received message', data.type);
                
                if (data.type === 'gantt_data' || data.type === 'data') {
                    setGanttData(data.data || data);
                    setIsGanttLoading(false);
                } else if (data.type === 'error') {
                    console.error('Gantt error:', data.message);
                    setIsGanttLoading(false);
                }
            } catch (error) {
                console.error('Gantt WebSocket: Error parsing message', error);
            }
        };

        ws.onclose = (event) => {
            clearTimeout(connectionTimeout);
            console.log(`Gantt WebSocket: Disconnected (code: ${event.code}, reason: ${event.reason})`);
            isConnectingRef.current.gantt = false;

            // Reconnect logic
            if (!isUnmountingRef.current && event.code !== 1000 && reconnectAttemptsRef.current.gantt < 5) {
                const delay = 5000;
                console.log(`Gantt WebSocket: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current.gantt + 1}/5)`);
                
                setTimeout(() => {
                    reconnectAttemptsRef.current.gantt++;
                    connectGanttWebSocket();
                }, delay);
            }
        };

        ws.onerror = (error) => {
            console.error('Gantt WebSocket: Error', error);
            setIsGanttLoading(false);
        };
    }, [selectedCrane, selectedOperationType, selectedGanttDate]);

    const loadGanttData = useCallback(() => {
        if (ganttWebSocketRef.current && ganttWebSocketRef.current.readyState === WebSocket.OPEN) {
            setIsGanttLoading(true);
            ganttWebSocketRef.current.send(JSON.stringify({
                type: 'get_gantt_data',
                crane_id: selectedCrane === 'all' ? '' : selectedCrane,
                date: selectedGanttDate,
                operation_type: selectedOperationType === 'all' ? '' : selectedOperationType
            }));
        } else {
            console.log('Gantt WebSocket not open, attempting to reconnect');
            connectGanttWebSocket();
        }
    }, [selectedCrane, selectedOperationType, selectedGanttDate, connectGanttWebSocket]);

    // Handle page change
    const handlePageChange = useCallback((page) => {
        if (page < 1 || page > totalPages) return;
        if (page === currentPageRef.current) return;
        
        console.log(`Changing to page ${page}`);
        setCurrentPage(page);
        currentPageRef.current = page;
        sendTableFilterRequest(page);
    }, [totalPages, sendTableFilterRequest]);

    // Apply filters
    const applyFilters = useCallback(() => {
        console.log('Applying filters, resetting to page 1');
        
        setCurrentPage(1);
        currentPageRef.current = 1;

        if (operationsWebSocketRef.current?.readyState === WebSocket.OPEN) {
            operationsWebSocketRef.current.send(JSON.stringify({
                type: 'apply_filters',
                crane_id: selectedCrane,
                operation_type: selectedOperationType,
                date_range: selectedDateRange
            }));
        }

        sendTableFilterRequest(1);
        loadGanttData();
        showNotification('Filters applied', 'success');
    }, [selectedCrane, selectedOperationType, selectedDateRange, sendTableFilterRequest, loadGanttData]);

    // Reset filters
    const resetFilters = useCallback(() => {
        console.log('Resetting filters');
        
        setSelectedCrane('all');
        setSelectedOperationType('all');
        setSelectedDateRange('today');
        setCurrentPage(1);
        currentPageRef.current = 1;

        setTimeout(() => {
            if (operationsWebSocketRef.current?.readyState === WebSocket.OPEN) {
                operationsWebSocketRef.current.send(JSON.stringify({
                    type: 'apply_filters',
                    crane_id: 'all',
                    operation_type: 'all',
                    date_range: 'today'
                }));
            }
            sendTableFilterRequest(1);
            loadGanttData();
            showNotification('Filters reset', 'success');
        }, 100);
    }, [sendTableFilterRequest, loadGanttData]);

    const handleLogout = () => {
        // Close all WebSockets
        if (operationsWebSocketRef.current) {
            operationsWebSocketRef.current.close(1000, "Logout");
        }
        if (tableWebSocketRef.current) {
            tableWebSocketRef.current.close(1000, "Logout");
        }
        if (ganttWebSocketRef.current) {
            ganttWebSocketRef.current.close(1000, "Logout");
        }
        
        localStorage.removeItem('isLoggedIn');
        sessionStorage.removeItem('Token');
        sessionStorage.removeItem('session_id');
        navigate('/login');
    };

    // Initialize connections - only once
    useEffect(() => {
        isUnmountingRef.current = false;
        fetchCranes();

        // Add a delay between connection attempts
        const initConnections = async () => {
            // Wait a bit before connecting
            await new Promise(resolve => setTimeout(resolve, 1000));
            connectOperationsWebSocket();
            
            await new Promise(resolve => setTimeout(resolve, 500));
            connectTableWebSocket();
            
            await new Promise(resolve => setTimeout(resolve, 500));
            connectGanttWebSocket();
        };
        
        initConnections();

        // Cleanup
        return () => {
            isUnmountingRef.current = true;
            
            // Clear all timeouts
            Object.keys(reconnectTimeoutsRef.current).forEach(key => {
                if (reconnectTimeoutsRef.current[key]) {
                    clearTimeout(reconnectTimeoutsRef.current[key]);
                    reconnectTimeoutsRef.current[key] = null;
                }
            });

            // Clear heartbeats
            Object.keys(heartbeatIntervalsRef.current).forEach(key => {
                if (heartbeatIntervalsRef.current[key]) {
                    clearInterval(heartbeatIntervalsRef.current[key]);
                    heartbeatIntervalsRef.current[key] = null;
                }
            });

            // Close all WebSockets
            const sockets = [
                { ref: operationsWebSocketRef, name: 'operations' },
                { ref: tableWebSocketRef, name: 'table' },
                { ref: ganttWebSocketRef, name: 'gantt' }
            ];
            
            sockets.forEach(({ ref }) => {
                if (ref.current) {
                    const ws = ref.current;
                    ref.current = null;
                    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                        ws.close(1000, "Component unmounting");
                    }
                }
            });
        };
    }, []);

    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * recordsPerPage + 1;
    const endRecord = Math.min(currentPage * recordsPerPage, totalRecords);

    const getPageNumbers = () => {
        const pages = [];
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, startPage + 4);

        if (endPage - startPage < 4 && startPage > 1) {
            startPage = Math.max(1, endPage - 4);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    useEffect(() => {
        if (selectedGanttDate) {
            const timeoutId = setTimeout(() => {
                if (ganttWebSocketRef.current?.readyState === WebSocket.OPEN) {
                    setIsGanttLoading(true);
                    ganttWebSocketRef.current.send(JSON.stringify({
                        type: 'get_gantt_data',
                        crane_id: selectedCrane === 'all' ? '' : selectedCrane,
                        date: selectedGanttDate,
                        operation_type: selectedOperationType === 'all' ? '' : selectedOperationType
                    }));
                } else {
                    connectGanttWebSocket();
                }
            }, 500);
            
            return () => clearTimeout(timeoutId);
        }
    }, [selectedGanttDate, selectedCrane, selectedOperationType, connectGanttWebSocket]);

    return (
        <div className="operations-log-container">
            <div className="dashboard-content">
                {/* Header */}
                <div className="header">
                    <div className="header-left">
                        <div className="header-logo">CraneIQ</div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className={`connection-status ${connectionStatus.overall}`}>
                            <span className={`status-indicator ${connectionStatus.overall}`}></span>
                            <span className="status-text">{connectionStatus.message}</span>
                        </div>

                        <div className="user-info-wrapper">
                            <div className="account-dropdown">
                                <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                                    {orgLogo ? (
                                                      <img
                                                        src={orgLogo}
                                                        alt="Organization Logo"
                                                        style={{
                                                          width: '32px',
                                                          height: '32px',
                                                          borderRadius: '50%',
                                                          objectFit: 'cover',
                                                          border: '2px solid #4361ee'
                                                        }}
                                                        onError={() => setOrgLogo(null)}
                                                      />
                                                    ) : (
                                                      <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                                    )}
                                    <span>{sessionStorage.getItem('username') || 'User'}</span>
                                    <FontAwesomeIcon icon={faCaretDown} />
                                </div>
                                {isDropdownOpen && (
                                    <div className="account-menu show">
                                        <div className="account-menu-item plan-info">
                                            <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                                            <span>Basic Plan</span>
                                        </div>
                                        <div className="account-menu-divider"></div>
                                        <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                            <FontAwesomeIcon icon={faUser} /> Profile
                                        </button>
                                        <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                            <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                                        </button>
                                        <div className="account-menu-divider"></div>
                                        <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                                            <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Page Title */}
                <div className="page-titles">
                    <h1>Operations Log</h1>
                    <p>Complete record of all crane operations</p>
                </div>

                {/* Filter Controls */}
                <div className="log-controls">
                    <div className="filter-group">
                        <label className="filter-label">Crane</label>
                        <select
                            className="filter-selectk"
                            value={selectedCrane}
                            onChange={(e) => setSelectedCrane(e.target.value)}
                        >
                            <option value="all">All Cranes</option>
                            {cranes.map(crane => (
                                <option key={crane.machine_id} value={crane.machine_id}>
                                    {crane.machine_name} ({crane.machine_id})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">Operation Type</label>
                        <select
                            className="filter-selectk"
                            value={selectedOperationType}
                            onChange={(e) => setSelectedOperationType(e.target.value)}
                        >
                            <option value="all">All Types</option>
                            <option value="hoist-up">Hoist Up</option>
                            <option value="hoist-down">Hoist Down</option>
                            <option value="ct-left">CT Left</option>
                            <option value="ct-right">CT Right</option>
                            <option value="lt-forward">LT Forward</option>
                            <option value="lt-reverse">LT Reverse</option>
                            <option value="emergency">Emergency</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">Date Range</label>
                        <select
                            className="filter-selectk"
                            value={selectedDateRange}
                            onChange={(e) => setSelectedDateRange(e.target.value)}
                        >
                            <option value="today">Today</option>
                            <option value="week">Last 7 Days</option>
                            <option value="month">Last 30 Days</option>
                        </select>
                    </div>

                    <div className="action-buttons" style={{ 
                        display: 'flex', 
                        flexDirection: 'row', 
                        flexWrap: 'nowrap', 
                        gap: '10px',
                        alignItems: 'center',
                        marginTop: '30px'
                        }}>
                        <button className="btn btn-primary" onClick={applyFilters}>
                            <FontAwesomeIcon icon={faFilter} /> Apply Filters
                        </button>
                        <button className="btn btn-secondary" onClick={resetFilters}>
                            <FontAwesomeIcon icon={faRedo} /> Reset
                        </button>
                        <button className="btn btn-refresh" onClick={applyFilters}>
                            <FontAwesomeIcon icon={faSyncAlt} /> Refresh
                        </button>
                    </div>
                </div>

                {/* Metrics Dashboard */}
                <div className="metricsdashboard">
                    <div className="metriccard">
                        <div className="metricheader">
                            <div className="metricicon icon-hoist"><FontAwesomeIcon icon={faArrowUp} /></div>
                            <div className="metrictitle">HOIST</div>
                        </div>
                        <div className="metricvalue">{metrics.total_hoist}</div>
                        <div className="metricdetails">
                            <div className="metricdetail">
                                <div className="detailvalue">{metrics.hoist_up}</div>
                                <div className="detaillabel">UP</div>
                            </div>
                            <div className="metricdetail">
                                <div className="detailvalue">{metrics.hoist_down}</div>
                                <div className="detaillabel">DOWN</div>
                            </div>
                        </div>
                    </div>

                    <div className="metriccard">
                        <div className="metricheader">
                            <div className="metricicon icon-travel"><FontAwesomeIcon icon={faExchangeAlt} /></div>
                            <div className="metrictitle">CT</div>
                        </div>
                        <div className="metricvalue">{metrics.total_ct}</div>
                        <div className="metricdetails">
                            <div className="metricdetail">
                                <div className="detailvalue">{metrics.ct_left}</div>
                                <div className="detaillabel">LEFT</div>
                            </div>
                            <div className="metricdetail">
                                <div className="detailvalue">{metrics.ct_right}</div>
                                <div className="detaillabel">RIGHT</div>
                            </div>
                        </div>
                    </div>

                    <div className="metriccard">
                        <div className="metricheader">
                            <div className="metricicon icon-travel"><FontAwesomeIcon icon={faArrowsAltH} /></div>
                            <div className="metrictitle">LT</div>
                        </div>
                        <div className="metricvalue">{metrics.total_lt}</div>
                        <div className="metricdetails">
                            <div className="metricdetail">
                                <div className="detailvalue">{metrics.lt_forward}</div>
                                <div className="detaillabel">FWD</div>
                            </div>
                            <div className="metricdetail">
                                <div className="detailvalue">{metrics.lt_reverse}</div>
                                <div className="detaillabel">REV</div>
                            </div>
                        </div>
                    </div>

                    <div className="metriccard" style={{ borderLeftColor: metrics.emergency > 0 ? '#e74a3b' : '#6c757d' }}>
                        <div className="metricheader">
                            <div className="metricicon icon-switch"><FontAwesomeIcon icon={faRandom} /></div>
                            <div className="metrictitle">SWITCH</div>
                        </div>
                        <div className="metricvalue">{metrics.emergency}</div>
                    </div>

                    <div className="metriccard">
                        <div className="metricheader">
                            <div className="metricicon icon-duration"><FontAwesomeIcon icon={faClock} /></div>
                            <div className="metrictitle">DURATION</div>
                        </div>
                        <div className="metricvalue">{metrics.total_duration}</div>
                    </div>

                    <div className="metriccard">
                        <div className="metricheader">
                            <div className="metricicon icon-load"><FontAwesomeIcon icon={faWeightHanging} /></div>
                            <div className="metrictitle">LOAD</div>
                        </div>
                        <div className="metricvalue">{metrics.total_load_weight} T</div>
                    </div>
                </div>

                {/* Operations Table */}
                <div className="logtable-container">
                    <table className="log-table">
                        <thead>
                            <tr>
                                <th>Timestamp</th>
                                <th>Crane ID</th>
                                <th>Operation</th>
                                <th>Duration</th>
                                <th>Load (kg)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {operations.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                                        No operations data available
                                    </td>
                                </tr>
                            ) : (
                                operations.map((operation, index) => {
                                    const operationType = operation.operation || 'Unknown';
                                    const operationTypeClass = getOperationTypeClass(operationType);
                                    const craneDisplay = operation.crane_id || 'Unknown Crane';

                                    return (
                                        <tr key={`${operation.timestamp}-${index}`}>
                                            <td>{formatTimestamp(operation.timestamp)}</td>
                                            <td><strong>{craneDisplay}</strong></td>
                                            <td>
                                                <span className={`operation-type ${operationTypeClass}`}>
                                                    {operationType}
                                                </span>
                                            </td>
                                            <td>{operation.duration || '00:00:00'}</td>
                                            <td>{operation.load !== undefined ? operation.load.toFixed(1) : '0.0'} kg</td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                     </table>

                    <div className="paginationcontainer">
                        <div className="pagination-info">
                            Showing {startRecord} to {endRecord} of {totalRecords} entries
                        </div>
                        <div className="paginationcontrols">
                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1 || totalRecords === 0}
                            >
                                <FontAwesomeIcon icon={faAngleDoubleLeft} />
                            </button>
                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || totalRecords === 0}
                            >
                                <FontAwesomeIcon icon={faAngleLeft} />
                            </button>
                            <div className="pagination-numbers">
                                {getPageNumbers().map(page => (
                                    <button
                                        key={page}
                                        className={`page-number ${page === currentPage ? 'active' : ''}`}
                                        onClick={() => handlePageChange(page)}
                                    >
                                        {page}
                                    </button>
                                ))}
                            </div>
                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                            >
                                <FontAwesomeIcon icon={faAngleRight} />
                            </button>
                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages || totalPages === 0}
                            >
                                <FontAwesomeIcon icon={faAngleDoubleRight} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Gantt Section */}
                <div className="gantt-section" style={{ marginBottom: '20px', marginTop: '20px' }}>
                    <div className="gantt-header" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '15px',
                        flexWrap: 'wrap',
                        gap: '10px'
                    }}>
                        <h3 style={{ color: '#2c3e50', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FontAwesomeIcon icon={faChartGantt} style={{ color: '#3498db' }} />
                            Operation Timeline
                        </h3>
                        <div className="gantt-controls" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <div className="gantt-date-picker" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#95a5a6' }} />
                                <input
                                    type="date"
                                    className="filter-selectk"
                                    style={{ width: '200px' }}
                                    value={selectedGanttDate}
                                    onChange={(e) => setSelectedGanttDate(e.target.value)}
                                />
                            </div>
                            <button className="btn btn-refresh" onClick={loadGanttData} style={{ padding: '6px 12px' }}>
                                <FontAwesomeIcon icon={faSyncAlt} />
                            </button>
                        </div>
                    </div>

                    <div className="gantt-legend" style={{
                        display: 'flex',
                        gap: '15px',
                        marginTop: '15px',
                        flexWrap: 'wrap',
                        padding: '12px 15px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '8px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                    }}>
                        <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '24px', height: '24px', background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)', borderRadius: '6px' }}></span>
                            <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: 500 }}>Hoist Up</span>
                        </div>
                        <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '24px', height: '24px', background: 'linear-gradient(135deg, #F44336 0%, #d32f2f 100%)', borderRadius: '6px' }}></span>
                            <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: 500 }}>Hoist Down</span>
                        </div>
                        <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '24px', height: '24px', background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)', borderRadius: '6px' }}></span>
                            <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: 500 }}>CT Left</span>
                        </div>
                        <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '24px', height: '24px', background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)', borderRadius: '6px' }}></span>
                            <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: 500 }}>CT Right</span>
                        </div>
                        <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '24px', height: '24px', background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)', borderRadius: '6px' }}></span>
                            <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: 500 }}>LT Forward</span>
                        </div>
                        <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '24px', height: '24px', background: 'linear-gradient(135deg, #FFC107 0%, #FFA000 100%)', borderRadius: '6px' }}></span>
                            <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: 500 }}>LT Reverse</span>
                        </div>
                        <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '24px', height: '24px', background: 'linear-gradient(135deg, #f44336 0%, #c62828 100%)', borderRadius: '6px', animation: 'pulse 2s infinite' }}></span>
                            <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: 500 }}>Emergency</span>
                        </div>
                    </div>

                    <div className="gantt-container" style={{ marginTop: '15px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', padding: '20px', overflowX: 'auto' }}>
                        {isGanttLoading ? (
                            <div style={{ textAlign: 'center', padding: '80px 50px' }}>
                                <div className="loading-spinner" style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTopColor: '#3498db', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' }}></div>
                                <p>Loading timeline data...</p>
                            </div>
                        ) : (
                            (() => {
                                if (!ganttData || (Array.isArray(ganttData) && ganttData.length === 0)) {
                                    return (
                                        <div style={{ textAlign: 'center', padding: '80px 50px', color: '#95a5a6' }}>
                                            <FontAwesomeIcon icon={faChartGantt} style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.5 }} />
                                            <h4 style={{ marginBottom: '10px' }}>No Operation Data</h4>
                                            <p style={{ fontSize: '0.9rem' }}>No crane operations found for the selected date and filters</p>
                                        </div>
                                    );
                                }

                                const dataArray = Array.isArray(ganttData) ? ganttData : (ganttData.data || []);
                                
                                if (dataArray.length === 0) {
                                    return (
                                        <div style={{ textAlign: 'center', padding: '80px 50px', color: '#95a5a6' }}>
                                            <FontAwesomeIcon icon={faChartGantt} style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.5 }} />
                                            <h4 style={{ marginBottom: '10px' }}>No Operation Data</h4>
                                            <p style={{ fontSize: '0.9rem' }}>No crane operations found for the selected date and filters</p>
                                        </div>
                                    );
                                }

                                const operationRows = [
                                    { id: 'hoist', label: 'Hoist Operations', icon: faArrowUp },
                                    { id: 'ct', label: 'Cross Travel (CT)', icon: faExchangeAlt },
                                    { id: 'lt', label: 'Long Travel (LT)', icon: faArrowsAltH },
                                    { id: 'emergency', label: 'Emergency Operations', icon: faExclamationTriangle }
                                ];

                                const groupedByCrane = {};
                                dataArray.forEach(op => {
                                    if (!groupedByCrane[op.crane_id]) {
                                        groupedByCrane[op.crane_id] = {
                                            crane_name: op.crane_name || op.crane_id,
                                            operations: { hoist: [], ct: [], lt: [], emergency: [] }
                                        };
                                    }

                                    const opType = op.operation_type || '';
                                    if (opType.includes('emergency')) {
                                        groupedByCrane[op.crane_id].operations.emergency.push(op);
                                    } else if (opType.includes('hoist')) {
                                        groupedByCrane[op.crane_id].operations.hoist.push(op);
                                    } else if (opType.includes('ct')) {
                                        groupedByCrane[op.crane_id].operations.ct.push(op);
                                    } else if (opType.includes('lt')) {
                                        groupedByCrane[op.crane_id].operations.lt.push(op);
                                    }
                                });

                                const getOperationColor = (opType) => {
                                    if (opType.includes('hoist-up')) return '#4CAF50';
                                    if (opType.includes('hoist-down')) return '#F44336';
                                    if (opType.includes('ct-left')) return '#2196F3';
                                    if (opType.includes('ct-right')) return '#FF9800';
                                    if (opType.includes('lt-forward')) return '#9C27B0';
                                    if (opType.includes('lt-reverse')) return '#FFC107';
                                    return '#f44336';
                                };

                                return (
                                    <div style={{ minWidth: '1000px' }}>
                                        {Object.keys(groupedByCrane).map(craneId => {
                                            const craneData = groupedByCrane[craneId];
                                            return (
                                                <div key={craneId} style={{ marginBottom: '20px', minWidth: '1000px' }}>
                                                    <div style={{
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        padding: '15px 20px',
                                                        fontWeight: 600,
                                                        color: 'white',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        borderRadius: '8px 8px 0 0'
                                                    }}>
                                                        <span>
                                                            <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '8px' }} />
                                                            {craneData.crane_name}
                                                            <span style={{ fontSize: '0.8rem', opacity: 0.8, marginLeft: '10px' }}>({craneId})</span>
                                                        </span>
                                                    </div>
                                                    <div style={{ padding: '10px', minWidth: '1000px' }}>
                                                        {operationRows.map(row => {
                                                            const operationsList = craneData.operations[row.id] || [];
                                                            const totalOps = operationsList.length;
                                                            const totalDuration = operationsList.reduce((sum, op) => sum + (op.duration_seconds || 0), 0);

                                                            return (
                                                                <div key={row.id} style={{
                                                                    marginBottom: '20px',
                                                                    position: 'relative',
                                                                    ...(row.id === 'emergency' && { backgroundColor: 'rgba(244, 67, 54, 0.05)', borderRadius: '4px' })
                                                                }}>
                                                                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                        <div style={{
                                                                            width: '140px',
                                                                            fontSize: '0.8rem',
                                                                            fontWeight: 600,
                                                                            color: '#2c3e50',
                                                                            paddingTop: '5px'
                                                                        }}>
                                                                            <FontAwesomeIcon icon={row.icon} style={{
                                                                                marginRight: '8px',
                                                                                ...(row.id === 'emergency' && { color: '#f44336' })
                                                                            }} />
                                                                            {row.label}
                                                                            {totalOps > 0 && (
                                                                                <div style={{ fontSize: '0.65rem', color: '#95a5a6', marginTop: '2px' }}>
                                                                                    {totalOps} ops, {formatDuration(totalDuration)}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div style={{
                                                                            flex: 1,
                                                                            position: 'relative',
                                                                            height: '40px',
                                                                            backgroundColor: '#f8f9fa',
                                                                            borderRadius: '4px'
                                                                        }}>
                                                                            {totalOps === 0 ? (
                                                                                <div style={{ position: 'absolute', top: '12px', left: '10px', color: '#95a5a6', fontSize: '0.7rem' }}>
                                                                                    No operations for this period
                                                                                </div>
                                                                            ) : (
                                                                                operationsList.map((op, idx) => {
                                                                                    const startTime = new Date(op.start_time);
                                                                                    const startTotalSeconds = (startTime.getHours() * 3600) + (startTime.getMinutes() * 60) + startTime.getSeconds();
                                                                                    const leftPercent = (startTotalSeconds / (24 * 3600)) * 100;
                                                                                    const lineColor = getOperationColor(op.operation_type);
                                                                                    const isEmergency = op.operation_type?.includes('emergency') || row.id === 'emergency';

                                                                                    return (
                                                                                        <div
                                                                                            key={idx}
                                                                                            className={`gantt-operation-line ${isEmergency ? 'operation-emergency' : ''}`}
                                                                                            style={{
                                                                                                position: 'absolute',
                                                                                                left: `${leftPercent}%`,
                                                                                                transform: 'translateX(-50%)',
                                                                                                width: isEmergency ? '3px' : '2px',
                                                                                                height: '40px',
                                                                                                top: 0,
                                                                                                backgroundColor: lineColor,
                                                                                                cursor: 'pointer',
                                                                                                transition: 'all 0.2s ease',
                                                                                                opacity: 0.8
                                                                                            }}
                                                                                            title={`${op.operation_type} | ${formatDurationWithSeconds(op.duration_seconds)} | Load: ${op.load || 0} kg`}
                                                                                        />
                                                                                    );
                                                                                })
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OperationsLog;